import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { withRouterProps } from '../../utils/with-router-props'
import { callApi, fetchApiDataIfNeeded } from '../../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../../constants/routes'
import { createSelector } from '@reduxjs/toolkit'
import SHSForm from '../../utils/shs-form'
import InputElement from '../../utils/input'
import Modal from '../../utils/modal'
import EditableDisplayField from '../../utils/editable-display-field'
import RoadTestPanel from '../road-test/panel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const selectInstallId = (state, installId) => installId
const selectInstallations = state => state.adminInstallations
const selectTemplateId = (state, templateId) => templateId
const selectTemplateGroups = state => state.templateGroups
const selectAnswers = state => state.adminAnswers
const selectQuestions = state => state.questions
const selectGroupQuestions = state => state.groupQuestions
const selectConfigs = state => state.configs

const makeInstallation = createSelector(
    [selectInstallations, selectInstallId],
    (installations, installId) => {
        return installations.find(installation => installation.id === installId)
    }
)

const makeAnswers = createSelector(
    [selectAnswers, selectInstallId],
    (answers, installId) => {
        return answers.filter(answer => answer.installationId === installId)
    }
)

const makeTemplateGroups = createSelector(
    [selectTemplateGroups, selectTemplateId],
    (templateGroups, templateId) => {
        return templateGroups.filter(group => group.templateId === templateId)
            .sort((a, b) => {
                if (a.displayOrder > b.displayOrder) {
                    return 1
                }
                return -1
            })
    }
)

const makeQuestionGroups = createSelector(
    [selectGroupQuestions, (state, tGroups) => tGroups],
    (groupQuestions, tGroups) => {
        const groupIds = tGroups.map(group => group.groupId)
        return groupQuestions.filter(question => groupIds.includes(question.groupId))
    }
)

const makeQuestions = createSelector(
    [selectQuestions],
    (questions) => questions
)

const makeInstallationStatusConfigs = createSelector(
    [selectConfigs],
    (configs) => configs.filter(c => c.field === 'INSTALLATION_STATUS').sort((a, b) => a.order > b.order ? 1 : -1)
)

const mapStateToProps = (state, ownProps) => {
    const installId = Number(ownProps.params.installId)
    const installation = makeInstallation(state, installId)
    const answers = makeAnswers(state, installId)
    const templateGroups = makeTemplateGroups(state, installation?.templateId)
    const questionGroups = makeQuestionGroups(state, templateGroups)
    const missingAnswerCount = questionGroups.filter(
        gq => !answers.find(a => a.groupQuestionId === gq.id)
    ).length

    return {
        installation,
        questions: makeQuestions(state),
        answers,
        templateGroups,
        questionGroups,
        missingAnswerCount,
        answerValues: state.adminAnswerValues,
        images: state.images,
        configs: makeInstallationStatusConfigs(state)
    }
}

const AdminInstallationView = ({ dispatch, navigate, params, installation, questions, answers, answerValues, templateGroups, questionGroups, missingAnswerCount, images, configs }) => {
    const installId = Number(params.installId)
    const templateId = installation?.templateId ? Number(installation.templateId) : null
    const [closedGroupIds, setClosedGroupIds] = useState([])
    const [rejectAnswerValueId, setRejectAnswerValueId] = useState(null)
    const [activeImageId, setActiveImageId] = useState(null)
    const [inputOpen, setInputOpen] = useState(false)
    const [activeTab, setActiveTab] = useState('installation')

    useEffect(() => {
        if (templateId) {
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('template-groups', 'LIST'), { filter: [`template_id|${templateId}`] }))
        }
    }, [dispatch, templateId])

    useEffect(() => {
        if (installId) {
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/installations', 'VIEW', installId)))
        }
    }, [dispatch, installId])

    useEffect(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('configs', 'LIST', null), { filter: ['field|INSTALLATION_STATUS'] }, true))
    }, [dispatch])

    const handleSyncAnswers = () => {
        dispatch(callApi(apiResourceEndpoint('admin/installations', 'UPDATE', installId, 'sync-answers'), {}))
            .then(() => {
                dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/installations', 'VIEW', installId), {}, true))
                dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('template-groups', 'LIST'), { filter: [`template_id|${templateId}`] }, true))
            })
    }

    const handleApprove = (answerValue) => {
        const payload = {
            status: 'APPROVE',
            answerValueId: answerValue.id
        }
        dispatch(callApi(apiResourceEndpoint('admin/installations', 'UPDATE', installation.id, 'approve-answer'), payload))
    }

    const handleReject = (formData, formId) => {
        dispatch(callApi(apiResourceEndpoint('admin/installations', 'UPDATE', installation.id, 'reject-answer'), formData, formId))
    }

    const statusOptions = installation && configs ? configs.map(status => {
        return { value: status.id, label: status.display }
    }) : []

    const activeModalImgId = answerValues
        .filter(av => av.answerId === activeImageId)
        .sort((a, b) => b.id - a.id)[0]?.imageId ?? ''

    const submitChangeStatus = (formData, formId) => {
        dispatch(callApi(apiResourceEndpoint('admin/installations', 'UPDATE', installation.id), formData, formId))
    }

    const handlePrint = () => {
        window.print()
    }

    const handleCSVDownload = () => {
        dispatch(callApi(apiResourceEndpoint('admin/installations', 'VIEW', installId, 'download-csv'), {}, null, false, true))
    }

    const handleDelete = () => {
        const confirm = window.confirm('Are you sure you want to delete this installation?')
        if (confirm) {
            dispatch(callApi(apiResourceEndpoint('admin/installations', 'DELETE', installId), {}, -1))
                .then((response) => {
                    if (response === 'SUCCESS') {
                        navigate(buildRoutePath(routes.adminInstallationIndex.path))
                    } else {
                        window.alert('Unable to delete installation. Please try again.')
                    }
                })
        }
    }

    if (!installation) {
        return <div>No installation found</div>
    }

    const renderGroupQuestions = (gQuestions) => {
        let allQuestions = []
        gQuestions.forEach(gQuestion => {
            const groupQuestion = questions.find(question => question.id === gQuestion.questionId)
            const answer = answers.find(a => a.groupQuestionId === gQuestion.id && a.installationId === installId)
            const answerValue = answerValues.find(av => av.answerId === answer?.id)
            let answerDisplay = null
            let showQuestion = true
            switch (groupQuestion?._computed.typeValue) {
            case 'IMAGE':
                const image = images.find(img => img.id === answerValue?.imageId)
                if (image) {
                    answerDisplay = (
                        <div onClick={() => setActiveImageId(answer.id)} className="image-wrapper" style={{ marginTop: '1rem' }}>
                            <img src={process.env.REACT_APP_API_URL + apiResourceEndpoint(`installations`, 'VIEW', installId, `image/${answer.id}`).url + `?style=thumbnail&iv=${image.id}`} alt="answer value" />
                        </div>
                    )
                }
                break
            case 'INFORMATIONAL':
                showQuestion = false
                break
            default:
                answerDisplay = <>{answerValue?.textValue}</>
            }
            if (showQuestion && groupQuestion) {
                allQuestions.push(
                    <div className="answer-review" key={`gquestion-${groupQuestion.id}`}>
                        <div className="question-answer">
                            <span className="question">{groupQuestion.label}</span>
                            <div className="answer">
                                {answerDisplay}
                            </div>
                        </div>
                        <div className="actions">
                            <FontAwesomeIcon className={`${answerValue?._computed?.statusValue === 'APPROVED' ? 'approve' : ''}`} icon="circle-check" onClick={() => handleApprove(answerValue)} title="Approve" />
                            <FontAwesomeIcon className={`${answerValue?._computed?.statusValue === 'REJECTED' ? 'reject' : ''}`} icon="circle-xmark" onClick={() => setRejectAnswerValueId(answerValue?.id)} title="Reject" />
                        </div>
                    </div>
                )
            }
        })
        return allQuestions
    }

    const toggleGroupOpenClose = (groupId) => {
        if (closedGroupIds.includes(groupId)) {
            setClosedGroupIds(closedGroupIds.filter(id => id !== groupId))
        } else {
            setClosedGroupIds([...closedGroupIds, groupId])
        }
    }

    const renderTemplateGroups = () => {
        let allGroups = []
        templateGroups.forEach(group => {
            const gQuestions = questionGroups.filter(gq => gq.groupId === group.groupId)
            const groupQuestionIds = gQuestions.map(gq => gq.id)
            const groupAnswers = answers.filter(answer => groupQuestionIds.includes(answer.groupQuestionId))
            const filteredGQuestions = gQuestions.filter(gq => groupAnswers.find(answer => answer.groupQuestionId === gq.id))
            if (filteredGQuestions.length > 0) {
                allGroups.push(
                    <div className="content-box" key={group.id}>
                        <div className="qGroup">
                            <div className="qGroup-header" onClick={() => toggleGroupOpenClose(group.id)}>
                                <h3>{group.groupTitle}</h3>
                                <FontAwesomeIcon className="toggle-icon" icon={closedGroupIds.includes(group.id) ? 'chevron-down' : 'chevron-up'} />
                            </div>
                            {closedGroupIds.includes(group.id) ? null : renderGroupQuestions(filteredGQuestions)}
                        </div>
                    </div>
                )
            }
        })
        return allGroups
    }


    return (
        <div className="admin-dashboard">
            <div className="header with-tabs">
                <div className="container">
                    <div className="header-details">
                        <EditableDisplayField
                            display={installation.label}
                            endpoint="admin/installations"
                            dataObj={installation}
                            extraClass="h2"
                            fields={[{
                                field: 'label',
                                type: 'text',
                                required: true
                            }]} />
                        <p>VIN: {installation.vin}</p>
                        <p>Template: {installation._computed.templateName}</p>
                        { !inputOpen &&
                        <>
                            <h3 className="clickable" style={{ marginLeft: 0 }} onClick={() => setInputOpen(!inputOpen)}>Status: {installation._computed.statusDisplay}</h3>
                        </>
                        }
                        { inputOpen &&
                        <div className="qGroup">
                            <SHSForm showCancel={true} cancelAction={() => setInputOpen(!inputOpen)} formClass="inline-form" submitFX={submitChangeStatus} successFX={() => setInputOpen(!inputOpen)}>
                                <InputElement
                                    type="valueSelect"
                                    id="statusId"
                                    options={statusOptions}
                                    label="Change Installation Status"
                                    default={installation.statusId}/>
                            </SHSForm>
                        </div>
                        }
                        <h3>Missing Answers: {missingAnswerCount > 0 ? missingAnswerCount : 'None'}</h3>
                        {missingAnswerCount > 0 && <button className="btn" onClick={handleSyncAnswers}>Sync Missing Answers</button>}
                    </div>
                    <div className="header-actions">
                        <button onClick={handlePrint} className="btn blue">
                            Print/Save PDF
                        </button>
                        <button onClick={handleCSVDownload} className="btn">
                            Download as CSV
                        </button>
                    </div>
                </div>
                <div className="tab-bar">
                    <button className={`tab ${activeTab === 'installation' ? 'active' : ''}`} onClick={() => setActiveTab('installation')}>Installation Answers</button>
                    <button className={`tab ${activeTab === 'road-test' ? 'active' : ''}`} onClick={() => setActiveTab('road-test')}>Road Test</button>
                </div>
            </div>
            <div className="content-wrapper">
                <div className="container">
                    {activeTab === 'installation' && 
                        <>
                            {renderTemplateGroups()}
                            <div className="content-box delete-wrapper">
                                <div className="btn small red" onClick={handleDelete}>
                                    DELETE INSTALLATION <FontAwesomeIcon icon="trash-alt" />
                                </div>
                            </div>
                        </>
                    }
                    {activeTab === 'road-test' && <RoadTestPanel installationId={installId} />}
                </div>
            </div>
            {rejectAnswerValueId && <Modal closeAction={() => setRejectAnswerValueId(null)}>
                <h2>Notes for Installer</h2>
                <SHSForm submitFX={handleReject} successFX={() => setRejectAnswerValueId(null)}>
                    <InputElement
                        type="textarea"
                        id="optimusNotes" />
                    <InputElement
                        type="hidden"
                        id="answerValueId"
                        default={rejectAnswerValueId} />
                </SHSForm>
            </Modal>}
            {activeImageId &&
                <Modal closeAction={() => setActiveImageId(null)}>
                    <img src={process.env.REACT_APP_API_URL + apiResourceEndpoint(`installations`, 'VIEW', installId, `image/${activeImageId}`).url + `?style=large&iv=${activeModalImgId}`} alt="answer value" />
                </Modal>
            }
        </div>
    )
}

export default withRouterProps(connect(mapStateToProps)(AdminInstallationView))
